import { useState, useRef, useEffect } from 'react';
import { Bell, Check, CheckCheck, ExternalLink } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/services/api';
import { humanize } from '@/utils/labels';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  link?: string | null;
  read: boolean;
  createdAt: string;
}

interface NotificationsResponse {
  data: Notification[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  // Unread count — poll every 30 seconds
  const { data: unreadData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.get<{ data: { count: number } }>('/notifications/unread-count').then(r => r.data.data),
    refetchInterval: 30_000,
  });
  const unreadCount = unreadData?.count ?? 0;

  // Notification list (only when open)
  const { data: notifData } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => api.get('/notifications?limit=20').then(r => r.data.data),
    enabled: open,
    refetchInterval: open ? 15_000 : false,
  });
  const notifications: Notification[] = Array.isArray(notifData?.data) ? notifData.data : [];

  // Mark single as read
  const markRead = useMutation({
    mutationFn: (id: string) => api.put(`/notifications/${id}/read`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Mark all as read
  const markAllRead = useMutation({
    mutationFn: () => api.put('/notifications/read-all'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  function handleNotificationClick(n: Notification) {
    if (!n.read) markRead.mutate(n.id);
    if (n.link) {
      navigate(n.link);
      setOpen(false);
    }
  }

  function timeAgo(dateStr: string) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  }

  const typeColorMap: Record<string, string> = {
    TASK_ASSIGNED: 'bg-info-100 text-info-700',
    TASK_OVERDUE: 'bg-danger-100 text-danger-700',
    DEADLINE_REMINDER: 'bg-warning-100 text-warning-700',
    FINDING_CREATED: 'bg-info-100 text-info-700',
    AUDIT_STARTED: 'bg-info-100 text-info-700',
    ASSESSMENT_COMPLETED: 'bg-success-100 text-success-700',
    POLICY_REVIEW_DUE: 'bg-warning-100 text-warning-700',
    POLICY_APPROVAL_REQUESTED: 'bg-warning-100 text-warning-800',
    POLICY_APPROVED: 'bg-success-100 text-success-700',
    POLICY_REJECTED: 'bg-danger-100 text-danger-700',
    REMEDIATION_ASSIGNED: 'bg-info-100 text-info-700',
    REMEDIATION_COMPLETED: 'bg-success-100 text-success-700',
    REMEDIATION_VERIFIED: 'bg-success-100 text-success-700',
    CONFIGURATION_DRIFT: 'bg-danger-100 text-danger-700',
    SYSTEM: 'bg-neutral-100 text-neutral-700',
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="relative flex items-center justify-center rounded-lg p-1.5 text-white/80 transition hover:bg-white/15 hover:text-white"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[rgb(var(--st-danger-fill))] px-1 text-xs font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="chrome-reset absolute right-0 top-full z-50 mt-2 w-96 overflow-hidden rounded-xl border border-gray-200 bg-surface text-gray-900 shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead.mutate()}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-400">
                No notifications yet
              </div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={`flex cursor-pointer gap-3 border-b border-gray-50 px-4 py-3 transition hover:bg-gray-50 ${
                    !n.read ? 'bg-blue-50/50' : ''
                  }`}
                >
                  {/* Unread dot */}
                  <div className="flex-shrink-0 pt-1.5">
                    {!n.read ? (
                      <div className="h-2 w-2 rounded-full bg-blue-500" />
                    ) : (
                      <div className="h-2 w-2" />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${typeColorMap[n.type] ?? 'bg-gray-100 text-gray-700'}`}>
                        {humanize(n.type)}
                      </span>
                      <span className="text-xs text-gray-400">{timeAgo(n.createdAt)}</span>
                    </div>
                    <p className="mt-0.5 text-sm font-medium text-gray-900">{n.title}</p>
                    <p className="mt-0.5 truncate text-xs text-gray-500">{n.message}</p>
                    {n.link && (
                      <span className="mt-1 inline-flex items-center gap-0.5 text-xs text-blue-600">
                        <ExternalLink className="h-3 w-3" />
                        View
                      </span>
                    )}
                  </div>

                  {/* Mark read button */}
                  {!n.read && (
                    <button
                      onClick={e => { e.stopPropagation(); markRead.mutate(n.id); }}
                      className="flex-shrink-0 self-center rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      aria-label="Mark as read"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
